import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Field, Text, VStack, Center, Spinner, Image } from '@chakra-ui/react';
import PasswordInput from '@/components/ui/foundation/PasswordInput';
import Logo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import { toaster } from '@/components/ui/toaster';
import ActivationService from '@/services/ActivationService';

const SetPasswordPage = ({ mode = 'activation' }) => {
    const { t } = useTranslation('login');
    const { token } = useParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState('loading');
    const [userInfo, setUserInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        ActivationService.verifyToken(token, mode)
            .then(data => {
                setUserInfo(data);
                setStatus('ready');
            })
            .catch(() => {
                setStatus('invalid');
            });
    }, [token]);

    const validate = () => {
        const e = {};
        if (password.length < 8) e.password = t('setPasswordPage.errorPasswordLength');
        if (password !== confirmPassword) e.confirmPassword = t('setPasswordPage.errorPasswordMismatch');
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }
        setErrors({});
        setIsSubmitting(true);
        try {
            if (mode === 'activation') {
                await ActivationService.setPassword(token, password);
            } else {
                await ActivationService.confirmPasswordReset(token, password);
            }
            toaster.create({ type: 'success', title: t('setPasswordPage.toastSuccessTitle') });
            navigate('/login');
        } catch {
            toaster.create({ type: 'error', title: t('setPasswordPage.toastErrorTitle') });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (status === 'loading') {
        return (
            <Center minH="100vh" w="100vw" bg="bg.primary">
                <VStack gap={4}>
                    <Spinner size="xl" color="blue.400" borderWidth="3px" />
                    <Text color="text.grey" fontSize={14}>{t('setPasswordPage.verifyingLink')}</Text>
                </VStack>
            </Center>
        );
    }

    if (status === 'invalid') {
        return (
            <Center minH="100vh" w="100vw" bg="bg.primary" px={{ base: 4, md: 0 }}>
                <Box w="full" maxW="400px" p={{ base: 6, md: 8 }} bg="bg.secondary" borderRadius={16}>
                    <VStack gap={6}>
                        <Text fontSize={20} color="white" fontWeight="bold" textAlign="center">
                            {t('setPasswordPage.linkInvalidTitle')}
                        </Text>
                        <Text color="text.grey" textAlign="center">
                            {t('setPasswordPage.linkInvalidMessage')}
                        </Text>
                        <Button variant="primary" w="full" onClick={() => navigate('/login')}>
                            {t('setPasswordPage.backToLogin')}
                        </Button>
                    </VStack>
                </Box>
            </Center>
        );
    }

    return (
        <Center minH="100vh" w="100vw" bg="bg.primary">
            <Box w="400px" p={8} bg="bg.secondary" borderRadius={16}>
                <VStack gap={6} as="form" onSubmit={handleSubmit}>
                    <Image src={Logo} w="70%" mb={2} />
                    <Text fontSize={24} color="white" fontWeight="bold">{t('setPasswordPage.title')}</Text>
                    {userInfo && (
                        <VStack gap={1}>
                            <Text color="text.grey" textAlign="center">
                                {t('setPasswordPage.greeting')} <Text as="span" color="white" fontWeight="semibold">{userInfo.firstName ?? userInfo.FirstName}</Text>,
                            </Text>
                            <Text color="text.grey" textAlign="center" fontSize={13}>
                                {t('setPasswordPage.greetingSuffix')} <Text as="span" color="blue.300">{userInfo.email ?? userInfo.Email}</Text>
                            </Text>
                        </VStack>
                    )}

                    <Field.Root required w="full" invalid={!!errors.password}>
                        <Field.Label color="white">{t('setPasswordPage.passwordLabel')} <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
                        {errors.password && <Field.ErrorText>{errors.password}</Field.ErrorText>}
                    </Field.Root>

                    <Field.Root required w="full" invalid={!!errors.confirmPassword}>
                        <Field.Label color="white">{t('setPasswordPage.confirmPasswordLabel')} <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                        {errors.confirmPassword && <Field.ErrorText>{errors.confirmPassword}</Field.ErrorText>}
                    </Field.Root>

                    <Button variant="primary" type="submit" w="full" loading={isSubmitting}>
                        {t('setPasswordPage.submit')}
                    </Button>
                </VStack>
            </Box>
        </Center>
    );
};

export default SetPasswordPage;
